import { useEffect, useRef, useState } from 'react';
import { fetchChatbotModuleVisibility } from '../api/fetchChatbotModuleVisibility';
import {
  parseGatewayUserInfo,
  userMatchesChatbotVisibility,
} from '../chatbotVisibilityMatch';
import { getAiChatApiBase } from '../config';

function initialState(moduleId) {
  if (!moduleId || !getAiChatApiBase()) {
    return { loading: false, visible: true };
  }
  return { loading: true, visible: false };
}

/**
 * Runtime gate: GET /chatbot/visibility/:moduleId vs gateway-userInfo.
 * When moduleId or API base is missing, skips the check (visible).
 *
 * @param {string | null | undefined} moduleId
 * @param {string | null | undefined} moduleKey — stable logical module id (pathname key)
 * @returns {{ loading: boolean, visible: boolean }}
 */
export function useChatbotModuleVisibility(moduleId, moduleKey = null) {
  const [state, setState] = useState(() => initialState(moduleId));
  const prevModuleKeyRef = useRef(moduleKey);

  useEffect(() => {
    const moduleKeyChanged = prevModuleKeyRef.current !== moduleKey;
    prevModuleKeyRef.current = moduleKey;

    if (!moduleId || !getAiChatApiBase()) {
      setState({ loading: false, visible: true });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({
      loading: true,
      visible: moduleKeyChanged ? false : prev.visible,
    }));

    fetchChatbotModuleVisibility(moduleId)
      .then((tokens) => {
        if (cancelled) {
          return;
        }
        const userInfo = parseGatewayUserInfo();
        setState({
          loading: false,
          visible: userMatchesChatbotVisibility(tokens, userInfo),
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setState({ loading: false, visible: false });
      });

    return () => {
      cancelled = true;
    };
  }, [moduleId, moduleKey]);

  return state;
}
